import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useI18n } from "@/i18n/I18nContext";
import SeoHead from "@/components/SeoHead";
import Ornament from "@/components/Ornament";
import ShareButtons from "@/components/ShareButtons";
import FetesMap from "@/components/FetesMap";
import { nativeShare } from "@/lib/native";

// Dedicated SEO landing for the 2027 Feasts. Distinct from `/presence`
// (product page): this page is a magazine-style teaser optimised for
// social sharing (OpenGraph cover image) and search engines (JSON-LD
// Event schema). Deep-links to the download bundle for prospects who
// arrive via search.

const OG_IMAGE = "/photos/og-fetes-2027.jpg";
const HERO = "/photos/prieure-sunset.jpg";
const BAY = "/photos/mont-bay-aerial.jpg";
const OSTENSIONS = "/photos/ostensions.jpg";
const FETES_DATE = new Date("2027-07-01T17:00:00+02:00");

const T = {
  fr: {
    title: "Fêtes populaires de saint Léonard 2027",
    description:
      "Millénaire du prieuré saint Léonard de Vains : cinq jours de célébrations populaires, ostensions médiévales et procession vers le Mont-Saint-Michel. Du 1ᵉʳ juillet 17 h au 5 juillet 11 h 2027.",
    kicker: "Millénaire · Année Européenne des Normands · 2027",
    heroTitle: "Fêtes populaires de saint Léonard",
    heroDates: "Du 1ᵉʳ juillet 17 h 00 au 5 juillet 11 h 00 · 2027",
    heroTag: "Vains · Baie du Mont-Saint-Michel · Normandie",
    lead:
      "Cinq jours de fête populaire, entre chapelle romane et grèves d'argent : ostensions médiévales, procession solennelle vers le Mont-Saint-Michel, conférences, ateliers et veillées internationales, en communion avec les villages de la Baie.",
    themesTitle: "Trois thèmes, un souffle unique",
    themes: [
      {
        badge: "01",
        title: "Libération",
        body: "Saint Léonard, patron des captifs et prisonniers. La Baie, seuil entre la Mort et la Résurrection, ouvre au pardon et à la liberté intérieure.",
      },
      {
        badge: "02",
        title: "Lumière",
        body: "Ostensions et cierges au coucher du soleil, création de fresques romanes, chants et vêpres — la lumière devient récit et rite partagés.",
      },
      {
        badge: "03",
        title: "Unité",
        body: "Jumelage avec l'abbaye San Leonardo di Siponto, rencontres européennes des jeunes, communion des villages de la Baie autour d'un millénaire d'hospitalité.",
      },
    ],
    scheduleTitle: "Cinq jours, dix moments forts",
    schedule: [
      { d: "Jeu. 1er juillet · 17 h", t: "Accueil dans les villages, remise des livrets & des écus" },
      { d: "Jeu. 1er juillet · 21 h", t: "Veillée locale — animations, chants, contes" },
      { d: "Ven. 2 juillet · 10 h 30", t: "Ouverture officielle des Fêtes, vernissage exposition" },
      { d: "Ven. 2 juillet · 15 h", t: "Ateliers de découverte sensorielle de la Baie" },
      { d: "Ven. 2 juillet · 18 h", t: "Vêpres des Anges — chœurs internationaux" },
      { d: "Sam. 3 juillet · 10 h", t: "Procession festive des reliques dans le bourg" },
      { d: "Sam. 3 juillet · 14 h", t: "Traversée des grèves vers le Mont-Saint-Michel" },
      { d: "Sam. 3 juillet · 20 h", t: "Requiem pour un Conquérant à l'Abbatiale" },
      { d: "Dim. 4 juillet · 9 h 30", t: "Messe solennelle — bénédiction des fresques et de l'autel" },
      { d: "Dim. 4 juillet · 19 h", t: "Soirée de Gala & feux d'artifice" },
    ],
    ctaTitle: "Recevez le programme complet",
    ctaBody: "Téléchargez le programme officiel, l'affiche A2 imprimable ou consultez le calendrier détaillé.",
    ctaProgramme: "Programme (PDF)",
    ctaPoster: "Affiche A2",
    ctaSchedule: "Calendrier détaillé",
    countdownKicker: "Vers l'ouverture des Fêtes",
    countdown: { days: "Jours", hours: "Heures", minutes: "Minutes", seconds: "Secondes", opened: "Les Fêtes ont commencé" },
    shareBtn: "Partager",
    shareCopied: "Lien copié !",
    mapKicker: "Sur les rives de la Baie",
    mapTitle: "Les lieux des Fêtes",
    mapBody: "Du Prieuré de Vains au Mont-Saint-Michel, en passant par Avranches, la Bergerie des Grèves et les villages d'accueil — cliquez sur chaque lieu pour découvrir ce qui s'y vivra.",
    mapLegendMain: "Lieux principaux",
    mapLegendVillages: "Villages d'accueil",
    mapLegendCrossing: "Traversée des grèves — sam. 3 juillet (horaires prévus)",
    partnersKicker: "Sous le haut patronage de",
    footNote:
      "Événement labellisé « Millénaire 2027 · Année Européenne des Normands » — Région Normandie · Abbayes de Normandie · Atout France.",
  },
  en: {
    title: "Popular Feasts of Saint Léonard 2027",
    description:
      "Millennium of the Priory of Saint Léonard of Vains: five days of popular celebration, medieval ostensions and procession to Mont-Saint-Michel. From 1 July 5 pm to 5 July 11 am, 2027.",
    kicker: "Millennium · European Year of the Normans · 2027",
    heroTitle: "Popular Feasts of Saint Léonard",
    heroDates: "From 1 July 5.00 pm to 5 July 11.00 am · 2027",
    heroTag: "Vains · Mont-Saint-Michel Bay · Normandy",
    lead:
      "Five days of popular festivity between Romanesque chapel and silver strand: medieval ostensions, solemn procession to Mont-Saint-Michel, lectures, workshops and international vigils in communion with the Bay villages.",
    themesTitle: "Three themes, one breath",
    themes: [
      { badge: "01", title: "Liberation", body: "Saint Léonard, patron of captives and prisoners. The Bay, threshold between Death and Resurrection, opens onto forgiveness and inner freedom." },
      { badge: "02", title: "Light", body: "Sunset ostensions and candles, Romanesque fresco creation, chants and vespers — light becomes shared story and rite." },
      { badge: "03", title: "Unity", body: "Twinning with the abbey of San Leonardo di Siponto, European Youth Encounters, communion of the Bay villages around a millennium of hospitality." },
    ],
    scheduleTitle: "Five days, ten highlights",
    schedule: [
      { d: "Thu. 1 July · 5 pm", t: "Reception in the villages, distribution of booklets & escus" },
      { d: "Thu. 1 July · 9 pm", t: "Local vigil — animations, songs, tales" },
      { d: "Fri. 2 July · 10.30 am", t: "Official opening of the Feasts, exhibition preview" },
      { d: "Fri. 2 July · 3 pm", t: "Sensory workshops discovering the Bay" },
      { d: "Fri. 2 July · 6 pm", t: "Vespers of Angels — international choirs" },
      { d: "Sat. 3 July · 10 am", t: "Festive procession of the relics through the village" },
      { d: "Sat. 3 July · 2 pm", t: "Crossing of the strands to Mont-Saint-Michel" },
      { d: "Sat. 3 July · 8 pm", t: "Requiem for a Conqueror at the Abbey" },
      { d: "Sun. 4 July · 9.30 am", t: "Solemn Mass — blessing of the frescoes and altar" },
      { d: "Sun. 4 July · 7 pm", t: "Gala Evening & fireworks" },
    ],
    ctaTitle: "Receive the full programme",
    ctaBody: "Download the official programme, the A2 printable poster or consult the detailed schedule.",
    ctaProgramme: "Programme (PDF)",
    ctaPoster: "A2 Poster",
    ctaSchedule: "Detailed schedule",
    countdownKicker: "Towards the opening of the Feasts",
    shareBtn: "Share",
    shareCopied: "Link copied!",
    mapKicker: "On the shores of the Bay",
    mapTitle: "The places of the Feasts",
    mapBody: "From the Priory of Vains to Mont-Saint-Michel, via Avranches, the Bergerie des Grèves and the host villages — click each place to discover what will happen there.",
    mapLegendMain: "Main sites",
    mapLegendVillages: "Host villages",
    mapLegendCrossing: "Crossing of the strands — Sat 3 July (planned times)",
    countdown: { days: "Days", hours: "Hours", minutes: "Minutes", seconds: "Seconds", opened: "The Feasts have begun" },
    partnersKicker: "Under the high patronage of",
    footNote:
      "Event officially labelled “Millennium 2027 · European Year of the Normans” — Region of Normandy · Abbeys of Normandy · Atout France.",
  },
};


function Countdown({ labels }) {
  const compute = () => {
    const diff = FETES_DATE.getTime() - Date.now();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, over: true };
    const days = Math.floor(diff / 86400000);
    const hours = Math.floor((diff % 86400000) / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    return { days, hours, minutes, seconds, over: false };
  };
  const [t, setT] = useState(compute);
  useEffect(() => {
    const id = setInterval(() => setT(compute()), 1000);
    return () => clearInterval(id);
  }, []);
  const pad = (n) => String(n).padStart(2, "0");
  if (t.over) {
    return (
      <div data-testid="landing-countdown-over"
           className="font-caps text-sm text-white/85 tracking-widest">
        · {labels.opened} ·
      </div>
    );
  }
  const cells = [
    { v: t.days, l: labels.days, k: "days" },
    { v: pad(t.hours), l: labels.hours, k: "hours" },
    { v: pad(t.minutes), l: labels.minutes, k: "minutes" },
    { v: pad(t.seconds), l: labels.seconds, k: "seconds" },
  ];
  return (
    <div data-testid="landing-countdown" className="flex flex-wrap gap-2 sm:gap-3">
      {cells.map((c) => (
        <div key={c.k}
             data-testid={`landing-countdown-${c.k}`}
             className="flex flex-col items-center min-w-[62px] sm:min-w-[74px] px-3 py-2"
             style={{
               background: "rgba(20,15,10,0.45)",
               border: "1px solid rgba(212,175,55,0.45)",
               backdropFilter: "blur(8px)",
             }}>
          <div className="font-display text-2xl sm:text-3xl text-white tabular-nums leading-none">
            {c.v}
          </div>
          <div className="font-caps text-[0.55rem] sm:text-[0.6rem] mt-1 text-white/70"
               style={{ letterSpacing: "0.2em" }}>
            {c.l}
          </div>
        </div>
      ))}
    </div>
  );
}


function HeroShareButton({ label, copiedLabel, text }) {
  const [copied, setCopied] = useState(false);
  const onShare = async () => {
    const hasSheet = typeof navigator !== "undefined" && !!navigator.share;
    await nativeShare({ title: document.title, text, url: window.location.href });
    if (!hasSheet) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    }
  };
  return (
    <button
      onClick={onShare}
      data-testid="landing-share-btn"
      className="btn btn-outline"
      style={{ borderColor: "var(--gold)", color: "var(--gold)" }}
    >
      {copied ? `✓ ${copiedLabel}` : `⤴ ${label}`}
    </button>
  );
}


function JsonLd() {  const { locale } = useI18n();
  useEffect(() => {
    const origin = window.location.origin;
    const json = {
      "@context": "https://schema.org",
      "@type": "Festival",
      name: "Fêtes populaires de saint Léonard 2027",
      alternateName: [
        "Popular Feasts of Saint Léonard",
        "Feste popolari di San Leonardo",
        "Fiestas populares de San Leonardo",
      ],
      startDate: "2027-07-01T17:00:00+02:00",
      endDate: "2027-07-05T11:00:00+02:00",
      eventStatus: "https://schema.org/EventScheduled",
      eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
      location: {
        "@type": "Place",
        name: "Prieuré saint Léonard de Vains",
        address: {
          "@type": "PostalAddress",
          streetAddress: "Prieuré saint Léonard",
          addressLocality: "Vains",
          postalCode: "50300",
          addressCountry: "FR",
        },
        geo: { "@type": "GeoCoordinates", latitude: 48.6656, longitude: -1.4736 },
      },
      image: [`${origin}/photos/og-fetes-2027.jpg`],
      description: T[locale === "en" ? "en" : "fr"].description,
      organizer: {
        "@type": "Organization",
        name: "Amis du Prieuré saint Léonard de Vains",
        url: origin,
      },
      isAccessibleForFree: true,
      offers: {
        "@type": "Offer",
        url: `${origin}/presence/inscription`,
        availability: "https://schema.org/InStock",
        price: "0",
        priceCurrency: "EUR",
        validFrom: "2026-01-01",
      },
    };
    let el = document.getElementById("jsonld-fetes-2027");
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = "jsonld-fetes-2027";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(json);
    return () => { el && el.remove(); };
  }, [locale]);
  return null;
}


export default function FetesPopulaires2027() {
  const { locale } = useI18n();
  const p = T[locale] || T.fr;
  const backend = process.env.REACT_APP_BACKEND_URL;

  return (
    <>
      <SeoHead
        titles={{ fr: T.fr.title, en: T.en.title }}
        descriptions={{ fr: T.fr.description, en: T.en.description }}
        keywords="Fêtes populaires de saint Léonard, millenium 2027, millénaire, Mont-Saint-Michel, Normandie, festival médiéval, traversée de la baie, procession, ostensions, Vains, Avranches, patrimoine normand, Guillaume le Conquérant, abbayes de Normandie"
        image={OG_IMAGE}
      />
      <JsonLd />

      {/* HERO */}
      <section
        className="relative"
        style={{
          minHeight: "72vh",
          background: `linear-gradient(rgba(20,15,10,0.35), rgba(20,15,10,0.85)), url(${HERO}) center/cover no-repeat`,
        }}
        data-testid="landing-hero"
      >
        <div className="container-prieure relative h-full flex flex-col justify-end pt-32 pb-24 sm:pt-40 sm:pb-32">
          <span
            className="font-caps text-[0.7rem] mb-3"
            style={{ color: "var(--gold)", letterSpacing: "0.28em" }}
          >
            {p.kicker}
          </span>
          <h1
            className="font-display text-4xl sm:text-6xl lg:text-7xl text-white leading-tight max-w-4xl"
            style={{ textShadow: "0 2px 24px rgba(0,0,0,0.55)" }}
            data-testid="landing-title"
          >
            {p.heroTitle}
          </h1>
          <p
            className="font-display italic text-xl sm:text-2xl text-white/90 mt-4 max-w-3xl"
            style={{ textShadow: "0 2px 14px rgba(0,0,0,0.55)" }}
          >
            {p.heroDates}
          </p>
          <p
            className="font-caps text-sm text-white/70 mt-2 max-w-3xl"
            style={{ letterSpacing: "0.18em" }}
          >
            {p.heroTag}
          </p>

          {/* COUNTDOWN — live ticking towards 2027-07-01 17:00 */}
          <div className="mt-8">
            <div className="font-caps text-[0.62rem] mb-2.5 text-white/75"
                 style={{ letterSpacing: "0.28em" }}>
              {p.countdownKicker}
            </div>
            <Countdown labels={p.countdown} />
          </div>

          <div className="flex flex-wrap gap-3 mt-8">
            <a
              href={`${backend}/api/presence/programme.pdf?locale=${locale}`}
              target="_blank" rel="noreferrer"
              data-testid="landing-cta-programme"
              className="btn btn-primary"
            >
              📄 {p.ctaProgramme}
            </a>
            <a
              href={`${backend}/api/presence/poster.pdf?locale=${locale}&size=A2`}
              target="_blank" rel="noreferrer"
              data-testid="landing-cta-poster"
              className="btn btn-outline"
              style={{ borderColor: "rgba(255,255,255,0.65)", color: "#fff" }}
            >
              🖼 {p.ctaPoster}
            </a>
            <Link
              to="/presence/planning-detaille"
              data-testid="landing-cta-schedule"
              className="btn btn-outline"
              style={{ borderColor: "rgba(255,255,255,0.65)", color: "#fff" }}
            >
              🗓 {p.ctaSchedule}
            </Link>
            <HeroShareButton label={p.shareBtn} copiedLabel={p.shareCopied} text={p.description} />
          </div>
        </div>
      </section>

      {/* LEAD */}
      <section className="py-20 sm:py-24">
        <div className="container-prieure max-w-3xl">
          <Ornament className="justify-start mb-6" />
          <p className="font-display text-2xl sm:text-3xl leading-snug"
             style={{ color: "var(--ink)" }}>
            {p.lead}
          </p>
        </div>
      </section>

      {/* THEMES */}
      <section
        className="py-24 section-edge-top section-edge-bottom"
        style={{ background: "var(--parchment)" }}
      >
        <div className="container-prieure">
          <h2 className="font-display text-3xl sm:text-4xl text-center"
              style={{ color: "var(--ink)" }}>
            {p.themesTitle}
          </h2>
          <Ornament className="justify-center mt-4 mb-12" />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {p.themes.map((th) => (
              <article key={th.badge} className="p-8"
                       style={{
                         background: "#fff",
                         border: "1px solid var(--sand)",
                         boxShadow: "0 3px 14px rgba(20,15,10,0.06)",
                       }}
                       data-testid={`landing-theme-${th.badge}`}>
                <div className="font-caps text-4xl"
                     style={{ color: "var(--gold)", letterSpacing: "0.1em" }}>
                  {th.badge}
                </div>
                <h3 className="font-display text-2xl mt-3"
                    style={{ color: "var(--burgundy)" }}>
                  {th.title}
                </h3>
                <p className="mt-3 text-base leading-relaxed"
                   style={{ color: "var(--ink-2)" }}>
                  {th.body}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* MAP — interactive places of the Feasts */}
      <section className="py-24" data-testid="landing-map-section">
        <div className="container-prieure">
          <div className="max-w-3xl mb-10">
            <div className="font-caps text-[0.72rem] mb-4" style={{ color: "var(--cinnabar)", letterSpacing: "0.24em" }}>
              {p.mapKicker.toUpperCase()}
            </div>
            <h2 className="font-display text-3xl sm:text-4xl" style={{ color: "var(--ink)" }}>
              {p.mapTitle}
            </h2>
            <p className="mt-4 text-base sm:text-lg leading-relaxed" style={{ color: "var(--ink-2)" }}>
              {p.mapBody}
            </p>
          </div>
          <FetesMap locale={locale} />
          <div className="flex flex-wrap gap-6 mt-4" data-testid="landing-map-legend">
            <span className="inline-flex items-center gap-2 font-caps text-[0.62rem]" style={{ color: "var(--ink-2)", letterSpacing: "0.16em" }}>
              <span style={{ width: 12, height: 12, background: "#7b1f2b", border: "2px solid #d4af37", transform: "rotate(45deg)", display: "inline-block" }} />
              {p.mapLegendMain.toUpperCase()}
            </span>
            <span className="inline-flex items-center gap-2 font-caps text-[0.62rem]" style={{ color: "var(--ink-2)", letterSpacing: "0.16em" }}>
              <span style={{ width: 10, height: 10, background: "#d4af37", border: "2px solid #fff", transform: "rotate(45deg)", display: "inline-block", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
              {p.mapLegendVillages.toUpperCase()}
            </span>
            <span className="inline-flex items-center gap-2 font-caps text-[0.62rem]" style={{ color: "var(--ink-2)", letterSpacing: "0.16em" }}>
              <span style={{ width: 26, height: 0, borderTop: "3px dashed #7b1f2b", display: "inline-block" }} />
              {p.mapLegendCrossing.toUpperCase()}
            </span>
          </div>
        </div>
      </section>

      {/* SCHEDULE HIGHLIGHTS */}
      <section
        className="py-24"
        style={{
          background: `linear-gradient(rgba(20,15,10,0.86), rgba(20,15,10,0.92)), url(${BAY}) center/cover no-repeat`,
        }}
      >
        <div className="container-prieure max-w-4xl">
          <h2 className="font-display text-3xl sm:text-4xl text-white text-center">
            {p.scheduleTitle}
          </h2>
          <Ornament className="justify-center mt-4 mb-12" />
          <ol className="space-y-4">
            {p.schedule.map((s, i) => (
              <li key={i}
                  className="flex flex-col sm:flex-row items-start gap-3 sm:gap-6 pb-4"
                  style={{ borderBottom: "1px solid rgba(212,175,55,0.25)" }}
                  data-testid={`landing-schedule-${i}`}>
                <div className="font-caps text-[0.75rem] flex-shrink-0"
                     style={{ color: "var(--gold)", letterSpacing: "0.16em", minWidth: "200px" }}>
                  {s.d}
                </div>
                <div className="font-display text-lg sm:text-xl text-white/90 leading-snug">
                  {s.t}
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="container-prieure max-w-3xl text-center">
          <img
            src={OSTENSIONS}
            alt=""
            className="mx-auto mb-10"
            style={{
              maxWidth: "220px",
              border: "1px solid var(--sand)",
              boxShadow: "0 3px 14px rgba(20,15,10,0.1)",
            }}
          />
          <h2 className="font-display text-3xl sm:text-4xl"
              style={{ color: "var(--ink)" }}>
            {p.ctaTitle}
          </h2>
          <p className="mt-4 text-base sm:text-lg leading-relaxed max-w-2xl mx-auto"
             style={{ color: "var(--ink-2)" }}>
            {p.ctaBody}
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-10">
            <a
              href={`${backend}/api/presence/programme.pdf?locale=${locale}`}
              target="_blank" rel="noreferrer"
              className="btn btn-primary"
              data-testid="landing-cta-programme-2"
            >
              📄 {p.ctaProgramme}
            </a>
            <a
              href={`${backend}/api/presence/programme-all.zip`}
              target="_blank" rel="noreferrer"
              className="btn btn-outline"
              data-testid="landing-cta-zip"
            >
              🌐 19 langues (ZIP)
            </a>
            <Link
              to="/presence"
              className="btn btn-outline"
              data-testid="landing-cta-presence"
            >
              → /presence
            </Link>
          </div>
          <div className="mt-10">
            <ShareButtons locale={locale} />
          </div>
          <p className="mt-12 font-caps text-[0.65rem]"
             style={{ color: "var(--gold)", letterSpacing: "0.24em" }}>
            {p.partnersKicker}
          </p>
          <div className="flex flex-wrap justify-center items-center gap-6 mt-6 opacity-90">
            <img src="/partners/millenium-2027.png" alt="Millenium 2027"
                 style={{ height: "48px", width: "auto" }} />
            <img src="/partners/region-normandie.png" alt="Région Normandie"
                 style={{ height: "48px", width: "auto" }} />
            <img src="/partners/abbayes-normandie.png" alt="Abbayes de Normandie"
                 style={{ height: "48px", width: "auto" }} />
          </div>
          <p className="mt-10 text-xs italic max-w-2xl mx-auto"
             style={{ color: "var(--ink-3)" }}>
            {p.footNote}
          </p>
        </div>
      </section>
    </>
  );
}
